import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { failedResponses } from '../errors/responses';
import { Bundle, PurchaseOrderBundle, PurchaseOrderBundleRequest } from './catalog/bundles';
import {
  BaseMargin,
  BaseMarginItem,
  Category,
  EffectiveDate,
  Filter,
  FilterPack,
  ImageDetails,
  Manufacturer,
  MarginFilter,
  MspCatalog,
  Price,
  PriceBreakdown,
  Product,
  ProductDiffs,
  ProductFilter,
  PropertyBag,
  ReplacedProduct
} from './catalog/types';
import ListResponse from './listResponse';

const c = initContract();

const categories = c.router({
  getCategories: {
    // Used by: Customer
    method: 'GET',
    path: '/categories',
    responses: {
      200: z.object({ items: z.array(Category) }),
      ...failedResponses
    },
    summary: 'Get all categories'
  },
  getCategory: {
    // Used by: Customer
    method: 'GET',
    path: '/categories/:id',
    responses: {
      200: Category,
      ...failedResponses
    },
    summary: 'Get category'
  },
  createCategory: {
    // Used by: Admin
    method: 'POST',
    path: '/categories',
    responses: {
      200: Category,
      ...failedResponses
    },
    body: Category.omit({ id: true }),
    summary: 'Create a new category'
  },
  updateCategory: {
    // Used by: Admin
    method: 'PUT',
    path: '/categories/:categoryId',
    responses: {
      200: Category,
      ...failedResponses
    },
    body: Category.omit({ id: true }),
    summary: 'Update a category'
  }
});

const manufacturers = c.router({
  // Used by: Customer
  getManufacturers: {
    method: 'GET',
    path: '/manufacturers',
    responses: {
      200: z.object({ items: z.array(Manufacturer) }),
      ...failedResponses
    },
    summary: 'Get all manufacturers'
  },
  getManufacturer: {
    method: 'GET',
    path: '/manufacturers/:id',
    responses: {
      200: Manufacturer,
      ...failedResponses
    },
    summary: 'Get all manufacturers'
  },
  // Used by: Admin
  createManufacturer: {
    method: 'POST',
    path: '/manufacturers',
    responses: {
      200: Manufacturer,
      ...failedResponses
    },
    body: z.object({
      name: z.string()
    }),
    summary: 'Create a new manufacturer'
  }
});

const products = c.router({
  // Used by: Admin
  createProduct: {
    method: 'POST',
    path: '/daascatalogitems',
    responses: {
      200: c.type<Omit<Product, 'price'>>(),
      ...failedResponses
    },
    body: z.object({
      name: z.string(),
      code: z.string(),
      productId: z.string(),
      description: z.any(),
      propertyBag: z.object({
        properties: PropertyBag,
        triggerTypes: z.array(z.string()).optional(),

      }),
      imageUri: ImageDetails.optional(),
      manufacturerId: z.string(),
      categoryId: z.string(),
      marginExcessWarning: z.boolean(),
      regionId: z.string(),
      isRateCardItem: z.boolean().optional()
    }),
    summary: 'Create a new product on the DaaS catalog'
  },
  // Used by: Admin
  bulkCreateProducts: {
    method: 'PUT',
    path: '/daascatalogitems/multi',
    responses: {
      200: c.type<Omit<Product, 'price'>[]>(),
      ...failedResponses
    },
    body: z.array(
      z.object({
        name: z.string(),
        productId: z.string(),
        code: z.string().optional(),
        description: z.any(),
        propertyBag: z.object({
          properties: PropertyBag,
          maxChargeableWeightKg: z.number().optional(),

        }),
        imageUri: ImageDetails.optional(),
        manufacturerId: z.string(),
        categoryId: z.string(),
        marginExcessWarning: z.boolean(),
        catalogPrice: Price,
        baseMargin: BaseMargin,
        regionId: z.string(),
        isRateCardItem: z.boolean().optional()
      })
    )
  },
  // Used by: Admin
  getCodesForNames: {
    method: 'POST',
    path: '/daascatalogitems/code',
    body: z.object({
      names: z.array(z.string())
    }),
    responses: {
      200: c.type<{
        items: { name: string; code: string }[];
      }>(),
      ...failedResponses
    }
  },
  // Used by: Admin
  addPrice: {
    method: 'POST',
    path: '/catalogprices',
    responses: {
      200: z.undefined(),
      ...failedResponses
    },
    body: z.object({
      daasCatalogItemId: z.string(),
      price: z.number(),
      effectiveDate: EffectiveDate
    })
  },
  // Used by: Admin
  addBaseMargin: {
    method: 'POST',
    path: '/basemargins',
    responses: {
      200: z.undefined(),
      ...failedResponses
    },
    body: z.object({
      daasCatalogItemId: z.string(),
      modifier: z.number(),
      min: z.number(),
      max: z.number(),
      effectiveDate: EffectiveDate
    })
  },
  // Used by: Admin
  getBaseMargin: {
    method: 'GET',
    path: '/daascatalogitems/:productId/basemargin',
    query: z.object({
      date: z.date()
    }),
    responses: {
      200: c.type<BaseMargin>(),
      ...failedResponses
    }
  },
  // Used by: Admin
  getPrice: {
    method: 'GET',
    path: '/daascatalogitems/:productId/catalogprice',
    query: z.object({
      date: z.date()
    }),
    responses: {
      200: c.type<Price>(),
      ...failedResponses
    }
  },
  // Used by: Admin
  getBaseMargins: {
    method: 'GET',
    path: '/basemargins',
    responses: {
      200: c.type<{
        items: BaseMarginItem[];
      }>(),
      ...failedResponses
    }
  },
  // Used by: Admin
  getPrices: {
    method: 'GET',
    path: '/catalogprices',
    responses: {
      200: c.type<{ items: Price[] }>(),
      ...failedResponses
    }
  },
  // Used by: Admin
  updateProduct: {
    method: 'PUT',
    path: '/daascatalogitems/:productId',
    responses: {
      200: c.type<Omit<Product, 'price'>>(),
      ...failedResponses
    },
    body: z.object({
      name: z.string(),
      code: z.string(),
      productId: z.string(),
      description: z.any(),
      propertyBag: z.object({
        properties: PropertyBag,
        triggerTypes: z.array(z.string()).optional(),

      }),
      imageUri: ImageDetails.optional(),
      manufacturerId: z.string(),
      categoryId: z.string(),
      marginExcessWarning: z.boolean(),
      regionId: z.string(),
      isRateCardItem: z.boolean().optional()
    })
  },
  // Used by: Admin
  deleteProduct: {
    method: 'DELETE',
    path: '/daascatalogitems/:productId',
    responses: {
      200: z.undefined(),
      ...failedResponses
    },
    body: z.undefined(),
    summary: 'Delete a product from the DaaS catalog'
  },
  // Used by: Admin
  getProduct: {
    method: 'GET',
    path: '/daascatalogitems/:productId',
    responses: {
      200: c.type<Omit<Product, 'price'>>()
    }
  },
  // Used by: Admin
  getRateCardTriggers: {
    method: 'GET',
    path: '/daascatalogitems/ratecards/triggers',
    responses: {
      200: z.array(z.string()),
      ...failedResponses
    }
  },
  // Used by: Admin, RateCard Engine
  getRateCards: {
    method: 'GET',
    path: '/daascatalogitems/ratecards',
    query: z.object({ 
      requestType: z.string().optional(),
      partnerId: z.string().optional(),
      companyId: z.string().optional()
    }),
    responses: {
      200: c.type<{ items: Product[] }>(),
      ...failedResponses
    }
  },
  // Used by: Admin
  createRateCard: {
    method: 'POST',
    path: '/daascatalogitems/ratecards',
    responses: {
      200: c.type<Omit<Product, 'price'>>(),
      ...failedResponses
    },
    body: z.object({
      name: z.string(),
      regionId: z.string(),
      catalogPrice: z.number(),
      triggerTypes: z.array(z.string()).optional(),
      basePackForRequestType: z.string().optional(),
      itemRateForRequestType: z.string().optional(),
      maxChargeableWeightKg: z.number().optional()
    })
  },
  // Used by: Admin
  bulkUpsertRateCards: {
    method: 'POST',
    path: '/daascatalogitems/ratecards/bulk',
    responses: {
      200: c.type<{ items: Omit<Product, 'price'>[] }>(),
      ...failedResponses
    },
    body: z.array(z.object({
      name: z.string(),
      productId: z.string().optional(),
      regionId: z.string(),
      catalogPrice: z.object({ price: z.number(), effectiveDate: z.object({ value: z.string() }).optional() }),
      triggerTypes: z.array(z.string()).optional(),
      basePackForRequestType: z.string().optional(),
      itemRateForRequestType: z.string().optional(),
      maxChargeableWeightKg: z.number().optional()
    }))
  },
  // Used by: Admin
  updateRateCard: {
    method: 'PUT',
    path: '/daascatalogitems/ratecards/:id',
    responses: {
      200: c.type<Omit<Product, 'price'>>(),
      ...failedResponses
    },
    body: z.object({
      name: z.string(),
      regionId: z.string(),
      catalogPrice: Price,
      triggerTypes: z.array(z.string()).optional(),
      basePackForRequestType: z.string().optional(),
      itemRateForRequestType: z.string().optional(),
      maxChargeableWeightKg: z.number().optional()
    })
  },
  // Used by: Admin
  getDaaSPriceHistory: {
    method: 'GET',
    path: '/daas/catalog/:productId/history',
    responses: {
      200: c.type<{ priceHistory: PriceBreakdown[] }>(),
      ...failedResponses
    }
  },
  // Used by: MSP
  getMSPPriceHistory: {
    method: 'GET',
    path: '/msp/:partnerId/catalog/:productId/history',
    responses: {
      200: c.type<{ priceHistory: PriceBreakdown[] }>(),
      ...failedResponses
    }
  },
  // Used by: Customer
  getCustomerPriceHistory: {
    method: 'GET',
    path: '/msp/:partnerId/customers/:companyId/catalog/:productId/history',
    query: z.object({
      date: z.date().optional()
    }),
    responses: {
      200: c.type<{ priceHistory: PriceBreakdown[] }>(),
      ...failedResponses
    }
  },
  // Used by: Customer
  getCustomerCatalogDiff: {
    method: 'GET',
    path: '/msp/:partnerId/customers/:companyId/catalog/approval/diff',
    responses: {
      200: ProductDiffs,
      ...failedResponses
    }
  },
  // Used by: Admin
  getDaaSProducts: {
    method: 'GET',
    path: '/daas/catalog/',
    responses: {
      200: c.type<{ items: Product[] }>(),
      ...failedResponses
    }
  },
  getDaaSProduct: {
    method: 'GET',
    path: '/daas/catalog/:id',
    responses: {
      200: Product,
      ...failedResponses
    }
  },
  // Used by: MSP
  getMSPProducts: {
    method: 'GET',
    path: '/msp/:partnerId/catalog',
    responses: {
      200: c.type<{ items: Product[] }>(),
      ...failedResponses
    }
  },
  getMSPProduct: {
    method: 'GET',
    path: '/msp/:partnerId/catalog/:id',
    responses: {
      200: Product,
      ...failedResponses
    }
  },
  // Used by: Customer
  getCustomerProducts: {
    method: 'GET',
    path: '/msp/:partnerId/customers/:companyId/catalog',
    query: z.object({
      date: z.date().optional(),
      includeRateCards: z.boolean().optional()
    }),
    responses: {
      200: c.type<{ items: Product[] }>(),
      ...failedResponses
    }
  },
  getCustomerProduct: {
    method: 'GET',
    path: '/msp/:partnerId/customers/:companyId/catalog/:id',
    query: z.object({
      date: z.date().optional()
    }),
    responses: {
      200: Product,
      ...failedResponses
    }
  },
  // Used by: Admin
  replaceProduct: {
    method: 'POST',
    path: '/productreplacements',
    responses: {
      200: c.type<ReplacedProduct>(),
      ...failedResponses
    },
    body: z.object({
      replacedProductId: z.string(),
      replacementProductId: z.string(),
      effectiveDate: EffectiveDate
    }),
    summary: 'Replace a product on the DaaS catalog'
  },
  // Used by: Admin
  getProductReplacements: {
    method: 'GET',
    path: '/productreplacements',
    responses: {
      200: c.type<{ items: ReplacedProduct[] }>(),
      ...failedResponses
    },
    summary: 'Get product replacements on the DaaS catalog'
  }
});

const catalog = c.router({
  //  Used by: Admin
  getDaaSCatalog: {
    method: 'GET',
    path: '/daascatalog',
    responses: {
      200: z.object({ filterPack: FilterPack }),
      ...failedResponses
    }
  },
  // Used by: MSP
  getMSPCatalog: {
    method: 'GET',
    path: '/msp/:partnerId/mspcatalog',
    responses: {
      200: MspCatalog,
      ...failedResponses
    }
  },
  // Used by: Admin
  getAllMSPCatalogs: {
    method: 'GET',
    path: '/mspcatalogs',
    responses: {
      200: ListResponse(MspCatalog),
      ...failedResponses
    }
  },
  getBaseCatalogForMsp: {
    method: 'GET',
    path: '/msp/:id/mspbasecatalog',
    responses: {
      200: MspCatalog,
      ...failedResponses
    }
  },
  // Used by: MSP
  getCustomerCatalog: {
    method: 'GET',
    path: '/msp/:partnerId/customer/:companyId/customercatalog',
    responses: {
      200: z.object({
        id: z.string(),
        company: z.object({
          id: z.string(),
          name: z.string()
        }),
        filterPack: FilterPack
      }),
      ...failedResponses
    }
  },
  // Used by: MSP
  getAllCustomerCatalogs: {
    method: 'GET',
    path: '/customercatalogs',
    responses: {
      200: z.object({
        items: z.array(
          z.object({
            id: z.string(),
            company: z.object({
              id: z.string(),
              name: z.string()
            }),
            filterPack: FilterPack
          })
        )
      }),
      ...failedResponses
    }
  },
  // Used by: Customer
  getCodesForNames: {
    method: 'POST',
    path: '/catalogbundles/code',
    body: z.object({
      names: z.array(z.string())
    }),
    responses: {
      200: c.type<{
        items: { name: string; code: string }[];
      }>(),
      ...failedResponses
    }
  },
  getCodesForBundleNames: {
    method: 'POST',
    path: '/customerbundles/code',
    body: z.object({
      names: z.array(z.string())
    }),
    responses: {
      200: c.type<{
        items: { name: string; code: string }[];
      }>(),
      ...failedResponses
    }
  },
  // Used by: Customer
  createBundle: {
    method: 'POST',
    path: '/msp/:mspId/customers/:customerId/customerbundles',
    body: Bundle.omit({ id: true }),
    responses: {
      200: Bundle,
      ...failedResponses
    }
  },
  // Used by: Customer
  deleteBundle: {
    method: 'DELETE',
    path: '/msp/:mspId/customers/:customerId/customerbundles/:id',
    responses: {
      200: z.undefined(),
      ...failedResponses
    },
    body: z.undefined()
  },
  // Used by: Customer
  editBundle: {
    method: 'PUT',
    path: '/msp/:mspId/customers/:customerId/customerbundles/:id',
    body: Bundle.omit({ id: true }),
    responses: {
      200: Bundle,
      ...failedResponses
    }
  },
  // Used by: Customer
  getJobBundle: {
    method: 'GET',
    path: '/catalogbundles/:id',
    responses: {
      200: Bundle,
      ...failedResponses
    }
  },
  // Used by: Customer
  getBundles: {
    method: 'GET',
    path: '/msp/:partnerId/customers/:companyId/customerbundles',
    responses: {
      200: z.object({
        items: z.array(Bundle),
        ...failedResponses
      })
    }
  },
  getPurchaseOrderBundles: {
    method: 'GET',
    path: '/msp/:partnerId/customers/:companyId/purchaseorderbundles',
    responses: {
      200: z.object({
        items: z.array(PurchaseOrderBundle),
        ...failedResponses
      })
    }
  },
  createCodesForPurchaseOrderBundles: {
    method: 'POST',
    path: '/purchaseorderbundles/code',
    body: z.object({
      names: z.array(z.string())
    }),
    responses: {
      200: c.type<{
        items: { name: string; code: string }[];
      }>(),
      ...failedResponses
    }
  },
  createPurchaseOrderBundle: {
    method: 'POST',
    path: '/msp/:mspId/customers/:customerId/purchaseorderbundles',
    body: PurchaseOrderBundleRequest,
    responses: {
      200: PurchaseOrderBundle,
      ...failedResponses
    }
  },
  updatePurchaseOrderBundle: {
    method: 'PUT',
    path: '/msp/:mspId/customers/:customerId/purchaseorderbundles/:id',
    body: PurchaseOrderBundleRequest,
    responses: {
      200: PurchaseOrderBundle,
      ...failedResponses
    }
  },
  deletePurchaseOrderBundle: {
    method: 'DELETE',
    path: '/msp/:mspId/customers/:customerId/purchaseorderbundles/:id',
    responses: {
      200: z.undefined(),
      ...failedResponses
    },
    body: z.undefined()
  },
  updatePurchaseOrderBundleState: {
    method: 'PUT',
    path: '/msp/:mspId/customers/:customerId/purchaseorderbundles/:id/state',
    body: z.object({
      state: z.number()
    }),
    responses: {
      200: z.object({
        status: z.string()
      }),
      ...failedResponses
    }
  },
  // Used by: Customer
  createCatalogueBundle: {
    method: 'POST',
    path: '/msp/:partnerId/customers/:companyId/catalogbundles',
    body: Bundle.omit({ id: true }),
    responses: {
      200: Bundle,
      ...failedResponses
    }
  },
  // Used by: Customer
  getCatalogueBundles: {
    method: 'GET',
    path: '/msp/:partnerId/customers/:companyId/catalogbundles',
    responses: {
      200: z.object({
        items: z.array(Bundle)
      }),
      ...failedResponses
    }
  }
});

const CreateFilterArgs = z.object({
  daasCatalogItemId: z.string().optional(),
  manufacturerId: z.string().optional(),
  categoryId: z.string().optional(),
  effectiveDate: EffectiveDate,
  invalidateDate: z.date().optional(),
  modifier: z.number()
});

export type CreateFilterArgs = z.infer<typeof CreateFilterArgs>;

const filters = c.router({
  // Used by: MSP
  getFilterPack: {
    method: 'GET',
    path: '/filterpacks/:filterPackId',
    responses: {
      200: FilterPack,
      ...failedResponses
    }
  },
  // Used by: MSP
  updateFilterPack: {
    method: 'PUT',
    path: '/filterpacks/:id',
    responses: {
      200: FilterPack,
      ...failedResponses
    },
    body: z.object({
      globalMarginModifier: z.number(),
      marginFilterIds: z.array(z.string()),
      productFilterIds: z.array(z.string())
    })
  },
  // Used by: MSP
  createMarginFilter: {
    method: 'POST',
    path: '/marginfilters',
    responses: {
      200: Filter,
      ...failedResponses
    },
    body: CreateFilterArgs
  },
  getMarginFilter: {
    method: 'GET',
    path: '/marginfilters/:id',
    responses: {
      200: MarginFilter,
      ...failedResponses
    }
  },
  deleteMarginFilter: {
    method: 'DELETE',
    path: '/marginfilters/:id',
    responses: {
      200: z.undefined(),
      ...failedResponses
    },
    body: z.undefined()
  },
  // Used by: MSP
  createProductFilter: {
    method: 'POST',
    path: '/productfilters',
    responses: {
      200: Filter,
      ...failedResponses
    },
    body: CreateFilterArgs.omit({ modifier: true })
  },
  getProductFilter: {
    method: 'GET',
    path: '/productfilters/:id',
    responses: {
      200: ProductFilter,
      ...failedResponses
    }
  },
  deleteProductFilter: {
    method: 'DELETE',
    path: '/productfilters/:id',
    responses: {
      200: z.undefined(),
      ...failedResponses
    },
    body: z.undefined()
  }
});

export default c.router({
  categories,
  manufacturers,
  products,
  catalog,
  filters
});
